import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, persistAuthPayload } from 'auth/auth.api';
import { shouldShowDevOriginNotice } from 'auth/devOrigin';
import { clearOtpSession } from 'auth/otpSession';
import {
  isConfirmedAuthFailure,
  isForbiddenAuthFailure,
  isRequestAborted,
  isTemporaryAuthFailure,
  isTokenPersistenceError,
} from 'auth/sessionClassification';
import {
  clearLoggedOutRedirect,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  hasStoredSession,
  markLoggedOut,
  TokenPersistenceError,
} from 'auth/tokenStore';
import { clearPrivateQueries } from 'core/api/queryKeys';
import { onSessionExpired, resetSessionExpiredNotification } from 'core/api/sessionEvents';
import { setLocale } from 'core/i18n/localeStore';
import { adaptUser } from 'auth/userAdapter';
import { isProfileComplete, needsLegalAcceptance } from 'core/routing/paths';

const AuthContext = createContext(null);

function safeClearTokens() {
  try {
    clearTokens();
  } catch {
    // In-memory session still ends even if storage teardown fails.
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('bootstrapping');
  const [sessionMessage, setSessionMessage] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const statusRef = useRef(status);
  statusRef.current = status;

  const applyUser = useCallback((nextUser) => {
    const user = adaptUser(nextUser);
    if (user?.preferred_locale) {
      setLocale(user.preferred_locale);
    }
    setUser(user);
    setStatus('authenticated');
    setSessionMessage(null);
    return user;
  }, []);

  const endSession = useCallback((message = null, { intentional = false } = {}) => {
    if (intentional) {
      markLoggedOut();
    }
    safeClearTokens();
    clearOtpSession();
    clearPrivateQueries(queryClient);
    setUser(null);
    setStatus('anonymous');
    setSessionMessage(message);
  }, [queryClient]);

  const clearSession = useCallback((message = null) => {
    endSession(message, { intentional: true });
  }, [endSession]);

  const expireSession = useCallback((message = 'auth.sessionExpired') => {
    if (statusRef.current === 'anonymous') {
      setSessionMessage((current) => current || message);
      return;
    }
    endSession(message, { intentional: false });
  }, [endSession]);

  const restoreSession = useCallback(async (signal, isCancelled) => {
    const cancelled = () => isCancelled() || signal?.aborted;

    const showTemporary = (message = 'auth.temporarilyUnavailable') => {
      if (cancelled()) {
        return null;
      }
      setStatus('temporarily_unavailable');
      setSessionMessage(message);
      return null;
    };

    const refreshThenMe = async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        if (!cancelled()) {
          expireSession();
        }
        return null;
      }
      try {
        const refreshed = await authApi.refresh(refreshToken);
        if (cancelled()) {
          return null;
        }
        persistAuthPayload(refreshed);
        const me = await authApi.me({ signal, skipRefresh: true });
        if (cancelled()) {
          return null;
        }
        return applyUser(me || refreshed?.user);
      } catch (error) {
        if (cancelled() || isRequestAborted(error)) {
          return null;
        }
        if (isTokenPersistenceError(error)) {
          return showTemporary('auth.storageUnavailable');
        }
        if (isForbiddenAuthFailure(error)) {
          setStatus('forbidden');
          setSessionMessage(null);
          return null;
        }
        if (isTemporaryAuthFailure(error)) {
          return showTemporary();
        }
        if (isConfirmedAuthFailure(error)) {
          expireSession();
          return null;
        }
        return showTemporary();
      }
    };

    if (!hasStoredSession()) {
      if (!cancelled()) {
        setStatus('anonymous');
      }
      return null;
    }

    if (!getAccessToken()) {
      return refreshThenMe();
    }

    try {
      const me = await authApi.me({ signal, skipRefresh: true });
      if (cancelled()) {
        return null;
      }
      return applyUser(me);
    } catch (error) {
      if (cancelled() || isRequestAborted(error)) {
        return null;
      }
      if (isTokenPersistenceError(error)) {
        return showTemporary('auth.storageUnavailable');
      }
      if (isForbiddenAuthFailure(error)) {
        if (!cancelled()) {
          setUser(null);
          setStatus('forbidden');
          setSessionMessage(null);
        }
        return null;
      }
      if (isTemporaryAuthFailure(error)) {
        return showTemporary();
      }
      if (isConfirmedAuthFailure(error)) {
        return refreshThenMe();
      }
      return showTemporary();
    }
  }, [applyUser, expireSession]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    restoreSession(controller.signal, () => cancelled);
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [restoreSession, retryNonce]);

  useEffect(() => onSessionExpired(() => expireSession('auth.sessionExpired')), [expireSession]);

  const retryBootstrap = useCallback(() => {
    setStatus('bootstrapping');
    setSessionMessage(null);
    setRetryNonce((value) => value + 1);
  }, []);

  const completeLogin = useCallback((payload) => {
    persistAuthPayload(payload);
    if (!getAccessToken()) {
      throw new TokenPersistenceError();
    }
    if (payload?.refresh_token && !getRefreshToken()) {
      throw new TokenPersistenceError();
    }
    clearLoggedOutRedirect();
    clearOtpSession();
    resetSessionExpiredNotification();
    const nextUser = applyUser({
      ...(payload.user || {}),
      next_step: payload.next_step,
      profile_complete: payload.profile_complete ?? payload.user?.profile_complete,
    });
    return nextUser;
  }, [applyUser]);

  const refreshUser = useCallback(async () => {
    const me = await authApi.me({ skipRefresh: false });
    return applyUser(me);
  }, [applyUser]);

  const logout = useCallback(async () => {
    markLoggedOut();
    try {
      await authApi.logout();
    } catch {
      // Clear local session even if the server logout call fails.
    }
    endSession(null, { intentional: true });
  }, [endSession]);

  const value = useMemo(() => ({
    user,
    status,
    sessionMessage,
    showDevOriginNotice: shouldShowDevOriginNotice(),
    isAuthenticated: status === 'authenticated',
    isProfileComplete: isProfileComplete(user),
    needsLegalAcceptance: needsLegalAcceptance(user),
    completeLogin,
    refreshUser,
    applyUser,
    logout,
    clearSession,
    expireSession,
    retryBootstrap,
  }), [applyUser, clearSession, completeLogin, expireSession, logout, refreshUser, retryBootstrap, sessionMessage, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
